/**
 * Platform Utilities for Capacitor Native Detection
 * 
 * Provides helper functions to detect platform and adjust behavior
 * for web vs native (iOS/Android) environments.
 */

/**
 * Check if running on Capacitor native platform (iOS or Android)
 */
export const isCapacitorNative = (): boolean => {
    if (typeof window === 'undefined') return false;
    return (window as any).Capacitor?.isNativePlatform?.() === true;
};

/**
 * Check if running specifically on iOS
 */
export const isCapacitorIOS = (): boolean => {
    if (typeof window === 'undefined') return false;
    return (window as any).Capacitor?.getPlatform?.() === 'ios';
};

/**
 * Check if running specifically on Android
 */
export const isCapacitorAndroid = (): boolean => {
    if (typeof window === 'undefined') return false;
    return (window as any).Capacitor?.getPlatform?.() === 'android';
};

/**
 * Get the current platform name
 */
export const getPlatformName = (): 'ios' | 'android' | 'web' => {
    if (typeof window === 'undefined') return 'web';
    return (window as any).Capacitor?.getPlatform?.() || 'web';
};

/**
 * Get adjusted timeout value for the current platform
 * Native platforms get longer timeouts due to slower Firestore performance
 * 
 * @param webTimeout - The timeout value for web (in milliseconds)
 * @param nativeMultiplier - Multiplier for native platforms (default 2x)
 * @returns Adjusted timeout value
 */
export const getQueryTimeout = (webTimeout: number, nativeMultiplier: number = 2): number => {
    return isCapacitorNative() ? webTimeout * nativeMultiplier : webTimeout;
};

/**
 * Get adjusted timeout for auth operations
 * Auth can be especially slow on native due to keychain access
 */
export const getAuthTimeout = (): number => {
    // Web: 5 seconds, Native: 20 seconds (increased from 12s to give more time for Firebase init + auth)
    return isCapacitorNative() ? 20000 : 5000;
};

/**
 * Get adjusted timeout for Firestore data queries
 */
export const getFirestoreQueryTimeout = (): number => {
    // Web: 10 seconds, Native: 25 seconds
    return isCapacitorNative() ? 25000 : 10000;
};

/**
 * Get adjusted timeout for watchlist data loading
 */
export const getWatchlistQueryTimeout = (): number => {
    // Web: 15 seconds, Native: 30 seconds
    return isCapacitorNative() ? 30000 : 15000;
};

/**
 * Check if running on a touch-enabled device
 */
export const isTouchDevice = (): boolean => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Check if running as a standalone PWA (installed to home screen)
 */
export const isStandalonePWA = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
};

/**
 * Check if pull-to-refresh should be enabled
 * Enabled for: touch devices, Capacitor apps, standalone PWAs
 */
export const shouldEnablePullToRefresh = (): boolean => {
    return isTouchDevice() || isCapacitorNative() || isStandalonePWA();
};

/**
 * Log platform information for debugging
 */
export const logPlatformInfo = (): void => {
    const platform = getPlatformName();
    const isNative = isCapacitorNative();

    console.log('📱 Platform Info:', {
        platform,
        isNative,
        isTouchDevice: isTouchDevice(),
        isStandalonePWA: isStandalonePWA(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
        capacitorVersion: typeof window !== 'undefined' ? (window as any).Capacitor?.version : 'N/A'
    });
};

/**
 * Initialize CSS custom properties for safe area handling
 *
 * On Capacitor **iOS**, WKWebView reports `env(safe-area-inset-*)` correctly (`ios.contentInset` is
 * `never`). Components use `var(--safe-area-inset-*)`, so we set those props to the same `env()`
 * values as web — **not** `0px` (that would strip notch padding now that we no longer use raw `env()`
 * in every component).
 * **Android** WebView often reports 0 for `env(safe-area-inset-top)` while content draws under the
 * status bar — we add class `capacitor-android` on `<html>` and override `--safe-area-inset-top` in
 * index.css so `var(--safe-area-inset-top)` / `var(--safe-area-inset-bottom)` match layout (do not use
 * StatusBar.setOverlaysWebView(false); it can leave white system bars on newer Android).
 *
 * On web/PWA, we use the actual env() values for proper safe area handling.
 *
 * This should be called early in the app lifecycle (e.g., in index.tsx or App.tsx)
 */
export const initializeSafeAreaHandling = (): void => {
    if (typeof document === 'undefined') return;

    const isNative = isCapacitorNative();
    const root = document.documentElement;

    if (isNative) {
        if (isCapacitorAndroid()) {
            // Android: --safe-area-inset-* comes from index.css (html.capacitor-android), not inline.
            root.classList.add('capacitor-android');
            console.log('📱 Safe area: Android native — safe-area CSS vars via index.css (capacitor-android)');
        } else {
            // iOS: mirror web — var(--safe-area-inset-*) must resolve to env() so headers match pre-refactor
            // behavior (components no longer use env() directly). Android-only overrides live in index.css.
            root.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top, 0px)');
            root.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom, 0px)');
            root.style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left, 0px)');
            root.style.setProperty('--safe-area-inset-right', 'env(safe-area-inset-right, 0px)');
            console.log('📱 Safe area: iOS native — CSS vars = env() (same as web/PWA)');
        }
    } else {
        // On web/PWA, use the actual safe area inset values
        root.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top, 0px)');
        root.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom, 0px)');
        root.style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left, 0px)');
        root.style.setProperty('--safe-area-inset-right', 'env(safe-area-inset-right, 0px)');
        console.log('📱 Safe area: Using CSS env() values for web/PWA');
    }
};
